import { isDictationContextEnabled } from '@/features/flags';
import { getDictationContext as getDictationContextApi } from '@/pages/api/scribeAPI';
import { type DictationContextResponse } from '@/types/dictation-context.types';
import { logger } from '@/utils/logger/logger';
import { getDictationDataConfig } from '@heidi/dictate/data';

export const DICTATION_CONTEXT_QUERY_KEY = ['DictationContext'] as const;

// staleTime is Infinity, so nothing refetches on its own; the cache is
// refreshed only by removeQueries on session-create (patient switch)
// pulls the latest rolling window of recent-patient terms.
const DICTATION_CONTEXT_STALE_TIME_MS = Infinity;

/**
 * The app cache crosses through the dictation data seam (its `getQueryClient`
 * member — the one way the app cache crosses; the app wiring binds the
 * singleton). LOCAL FAIL-LOUD rather than a silent `undefined`: a missing
 * getter would hand a module-scope invalidator no cache and no error, which is
 * the trap the seam member's own doc names.
 */
const getQueryClient = () => {
  const client = getDictationDataConfig().getQueryClient?.();
  if (!client) {
    throw new Error(
      '[dictationBubble/store] query client not wired — configureDictationData must supply getQueryClient'
    );
  }
  return client;
};

async function fetchDictationContextTerms(): Promise<string[]> {
  try {
    return await getQueryClient().fetchQuery({
      queryKey: DICTATION_CONTEXT_QUERY_KEY,
      queryFn: async ({ signal }): Promise<string[]> => {
        const { data } = await getDictationContextApi(signal);
        return Array.isArray(data.terms) ? data.terms : [];
      },
      staleTime: DICTATION_CONTEXT_STALE_TIME_MS,
    });
  } catch (error) {
    logger.warn({
      event_type: 'DICTATION_CONTEXT_FETCH_FAILED',
      details: { error },
    });
    return [];
  }
}

/**
 * Remove cached dictation-context terms so too-early synchronous reads return
 * null instead of stale terms. The next {@link resolveDictationContext} call
 * will fetch fresh terms.
 */
export function invalidateDictationContext(): void {
  getQueryClient().removeQueries({
    queryKey: DICTATION_CONTEXT_QUERY_KEY,
  });
}

function normalizeContextTerms(terms: DictationContextResponse['terms']): string | null {
  const dictationContext = terms
    .map((term) => term.trim())
    .filter((term) => term.length > 0)
    .join('\n');

  return dictationContext.length > 0 ? dictationContext : null;
}

export async function resolveDictationContext(): Promise<string | null> {
  if (!isDictationContextEnabled()) return null;

  const terms = await fetchDictationContextTerms();
  return normalizeContextTerms(terms);
}

export function readDictationContextFromCache(): string | null {
  if (!isDictationContextEnabled()) return null;

  const terms = getQueryClient().getQueryData<string[]>(DICTATION_CONTEXT_QUERY_KEY);
  if (!terms) return null;

  return normalizeContextTerms(terms);
}
